import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSucursalesUsuarioContext } from '../contexts/SucursalesUsuarioContext';
import { persistGlobalNroSuc } from './useSucursalesVentaFiltro';
import { resolveSucursalPreferidaNro } from '../utils/sucursalPreferida';

type SucursalLike = { nroSuc: number };

export function useSucursalAltaFormulario(options: {
  nroSuc: number;
  setNroSuc: (nroSuc: number) => void;
  sucursales: SucursalLike[];
  loadingSucursales: boolean;
  enabled?: boolean;
}): { onSucursalChange: (nroSuc: number) => void } {
  const { nroSuc, setNroSuc, sucursales, loadingSucursales, enabled = true } = options;
  const { preferidaNro, loading: loadingPreferida } = useSucursalesUsuarioContext();
  const location = useLocation();
  const preferidaAplicadaRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      preferidaAplicadaRef.current = false;
      return;
    }
    preferidaAplicadaRef.current = false;
    setNroSuc(0);
  }, [enabled, location.key, setNroSuc]);

  useEffect(() => {
    if (!enabled) return;
    if (loadingSucursales || loadingPreferida || sucursales.length === 0) return;
    if (preferidaAplicadaRef.current && Number(nroSuc) > 0) return;

    const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
    if (preferida != null) {
      preferidaAplicadaRef.current = true;
      if (Number(nroSuc) !== preferida) {
        setNroSuc(preferida);
      }
      persistGlobalNroSuc(preferida);
      return;
    }

    preferidaAplicadaRef.current = true;
    if (Number(nroSuc) > 0) return;
    let stored = 0;
    try {
      stored = Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || '0');
    } catch {
      stored = 0;
    }
    const inList = (n: number) =>
      Number.isFinite(n) && n > 0 && sucursales.some((s) => Number(s.nroSuc) === n);
    const pick = inList(stored) ? stored : Number(sucursales[0]?.nroSuc ?? 0);
    if (pick > 0) setNroSuc(pick);
  }, [
    enabled,
    loadingSucursales,
    loadingPreferida,
    sucursales,
    preferidaNro,
    nroSuc,
    setNroSuc,
    location.key,
  ]);

  useEffect(() => {
    const preferida = resolveSucursalPreferidaNro(preferidaNro, sucursales);
    if (preferida != null) {
      persistGlobalNroSuc(preferida);
    }
  }, [preferidaNro, sucursales]);

  const onSucursalChange = useCallback(
    (value: number) => {
      preferidaAplicadaRef.current = true;
      setNroSuc(value);
    },
    [setNroSuc]
  );

  return { onSucursalChange };
}
